import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { stockApi, productsApi } from '../api/client';
import Input from '../components/Input';
import Button from '../components/Button';
import Card from '../components/Card';
import { parseQrCode } from '../utils/qrParser';

// Функция форматирования количества: целые числа без десятичной части, дробные с одним знаком
const formatQty = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0';
  return num % 1 === 0 ? num.toString() : num.toFixed(1);
};

const StockIn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [productId, setProductId] = useState(searchParams.get('product_id') || null);
  const [product, setProduct] = useState(null);
  const [itemCodeInput, setItemCodeInput] = useState('');
  const [productsList, setProductsList] = useState(null); // Список товаров с разными диоптриями
  const [items, setItems] = useState([{ dioptry: '', qty: '' }]); // Массив позиций для прихода

  // Варианты диоптрий
  const dioptryOptions = [];
  dioptryOptions.push(0, 1.00, 1.25, 1.50, 1.75);
  for (let i = 2.00; i <= 5.00; i += 0.25) {
    dioptryOptions.push(parseFloat(i.toFixed(2)));
  }
  for (let i = 5.50; i <= 9.00; i += 0.50) {
    dioptryOptions.push(parseFloat(i.toFixed(2)));
  }

  const { data: productData } = useQuery({
    queryKey: ['product', productId],
    queryFn: async () => {
      const response = await productsApi.getById(productId);
      return response.data;
    },
    enabled: !!productId,
  });

  const { data: stockData } = useQuery({
    queryKey: ['stock', productId],
    queryFn: async () => {
      const response = await stockApi.getStock(productId);
      return response.data;
    },
    enabled: !!productId,
  });

  useEffect(() => {
    if (productData) {
      setProduct(productData);
    }
  }, [productData]);

  const handleSearchByItemCode = async () => {
    if (!itemCodeInput.trim()) {
      alert('Введите артикул товара');
      return;
    }

    try {
      const response = await productsApi.getByItemCode(itemCodeInput.toUpperCase());
      const data = response.data;
      
      if (!data) {
        alert('Товар не найден');
        return;
      }
      
      // Если это массив товаров (несколько с одинаковым артикулом, но разными диоптриями)
      if (Array.isArray(data) && data.length > 1) {
        setProductsList(data);
        setProduct(null);
        setProductId(null);
      } else {
        // Один товар или массив с одним элементом
        const productToAdd = Array.isArray(data) ? data[0] : data;
        if (!productToAdd || !productToAdd.id) {
          alert('Ошибка: некорректные данные товара');
          return;
        }
        setProduct(productToAdd);
        setProductId(productToAdd.id);
        setProductsList(null);
      }
      setItemCodeInput('');
    } catch (error) {
      console.error('Ошибка поиска товара:', error);
      if (error.response?.status === 404) {
        if (confirm('Товар не найден. Создать новый товар?')) {
          navigate(`/products/new?item_code=${itemCodeInput.toUpperCase()}`);
        }
      } else {
        alert('Ошибка поиска товара: ' + (error.message || 'Неизвестная ошибка'));
      }
    }
  };

  const handleSelectProduct = (selectedProduct) => {
    setProduct(selectedProduct);
    setProductId(selectedProduct.id);
    setProductsList(null);
    setItems([{ dioptry: '', qty: '' }]); // Сбрасываем позиции при выборе нового товара
  };

  const addItem = () => {
    setItems([...items, { dioptry: '', qty: '' }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  const mutation = useMutation({
    mutationFn: async (itemsToAdd) => {
      // Выполняем все запросы последовательно
      const results = [];
      for (const item of itemsToAdd) {
        const dioptryValue = item.dioptry && item.dioptry.trim() !== '' ? parseFloat(item.dioptry) : null;
        const qtyValue = parseFloat(item.qty);
        if (qtyValue > 0) {
          const result = await stockApi.stockIn(productId, qtyValue, dioptryValue);
          results.push(result);
        }
      }
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['stock', productId]);
      queryClient.invalidateQueries(['products']);
      const validItems = items.filter(item => parseFloat(item.qty || 0) > 0);
      alert(`Успешно добавлено ${validItems.length} ${validItems.length === 1 ? 'позиция' : validItems.length < 5 ? 'позиции' : 'позиций'} на склад!`);
      setItems([{ dioptry: '', qty: '' }]);
      setProduct(null);
      setProductId(null);
      setProductsList(null);
    },
    onError: (error) => {
      alert(error.response?.data?.error || 'Ошибка при добавлении товара на склад');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!productId) {
      alert('Выберите товар');
      return;
    }

    // Фильтруем валидные позиции
    const validItems = items.filter(item => {
      const qtyValue = parseFloat(item.qty || 0);
      return qtyValue > 0;
    });

    if (validItems.length === 0) {
      alert('Добавьте хотя бы одну позицию с количеством больше нуля');
      return;
    }

    mutation.mutate(validItems);
  };

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Приход товара</h1>

      {/* Поиск по артикулу */}
      {!product && !productsList && (
        <div className="mb-4">
          <Input
            label="Артикул"
            value={itemCodeInput}
            onChange={(e) => setItemCodeInput(e.target.value.toUpperCase())}
            placeholder="Введите артикул товара"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearchByItemCode();
              }
            }}
          />
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            onClick={handleSearchByItemCode}
            disabled={!itemCodeInput.trim()}
          >
            🔍 Найти по артикулу
          </Button>
        </div>
      )}

      {/* Список товаров с разными диоптриями */}
      {productsList && productsList.length > 1 && (
        <Card className="mb-4">
          <h3 className="text-lg font-semibold mb-3">
            Найдено товаров с артикулом {productsList[0]?.item_code || 'N/A'}: {productsList.length}
          </h3>
          <p className="text-sm text-gray-600 mb-3">Выберите товар с нужными диоптриями:</p>
          <div className="space-y-2">
            {productsList.map((prod) => (
              <button
                key={prod.id}
                onClick={() => handleSelectProduct(prod)}
                className="w-full p-3 text-left border border-gray-300 rounded-lg hover:bg-blue-50 hover:border-blue-500 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{prod.name || 'Без названия'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-blue-600">
                      {parseFloat(prod.price || 0).toFixed(2)} ₸
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Остаток: {formatQty(prod.stock || 0)}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
          <Button
            variant="secondary"
            size="md"
            className="w-full mt-3"
            onClick={() => {
              setProductsList(null);
              setItemCodeInput('');
            }}
          >
            Назад
          </Button>
        </Card>
      )}

      {/* Выбранный товар */}
      {product && (
        <>
          <Card className="mb-4">
            <h3 className="text-lg font-semibold mb-2">{product.name || 'Без названия'}</h3>
            {product.item_code && (
              <p className="text-sm font-bold text-blue-600 mb-1 font-mono">
                Артикул: {product.item_code}
              </p>
            )}
            <p className="text-lg font-bold text-blue-600 mt-2">
              {parseFloat(product.price || 0).toFixed(2)} ₸
            </p>
            
            {/* Остатки по диоптриям */}
            {stockData?.stock_by_dioptry && stockData.stock_by_dioptry.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-sm font-semibold text-gray-700 mb-2">Остатки по диоптриям:</p>
                <div className="space-y-1">
                  {stockData.stock_by_dioptry.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        {item.dioptry === null ? 'Без диоптрий' : (item.dioptry === 0 || item.dioptry === '0' ? '0' : "-" + parseFloat(item.dioptry).toFixed(2))}:
                      </span>
                      <span className="font-bold text-gray-900">{formatQty(item.stock)} шт.</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>

          <form onSubmit={handleSubmit}>
            <Card className="mb-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-gray-900">Позиции для прихода</h3>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={addItem}
                >
                  + Добавить строку
                </Button>
              </div>
              
              <div className="space-y-3">
                {items.map((item, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-gray-700 min-w-[60px]">
                        #{index + 1}
                      </span>
                      {items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="ml-auto text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          ✕ Удалить
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Диоптрии
                        </label>
                        <select
                          value={item.dioptry}
                          onChange={(e) => updateItem(index, 'dioptry', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        >
                          <option value="">Не указано</option>
                          {dioptryOptions.map((value) => (
                            <option key={value} value={value}>
                              {value === 0 ? '0' : "-" + value.toFixed(2)}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Количество
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          min="0.1"
                          value={item.qty}
                          onChange={(e) => updateItem(index, 'qty', e.target.value)}
                          placeholder="0"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {mutation.error && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                {mutation.error.response?.data?.error || 'Произошла ошибка'}
              </div>
            )}

            <Button
              type="submit"
              variant="success"
              size="lg"
              className="w-full"
              disabled={!productId || mutation.isPending}
            >
              {mutation.isPending ? 'Добавление...' : `Добавить все на склад (${items.filter(item => parseFloat(item.qty || 0) > 0).length} позиций)`}
            </Button>
          </form>

          <Button
            variant="secondary"
            size="md"
            className="w-full mt-3"
            onClick={() => {
              setProduct(null);
              setProductId(null);
              setItems([{ dioptry: '', qty: '' }]);
              setProductsList(null);
            }}
          >
            Изменить товар
          </Button>
        </>
      )}
    </div>
  );
};

export default StockIn;
